import axios from "axios";
import { Capacitor } from "@capacitor/core";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * The correct origin to pass to any THIRD-PARTY REDIRECT flow — Stripe
 * checkout, Emergent Google Auth callback, etc.
 *
 * On the web, `window.location.origin` is a real https:// URL and the
 * external service can redirect to it as expected.
 *
 * On Capacitor native (iOS/Android), `window.location.origin` is
 * `capacitor://localhost` — a scheme Safari can't navigate to. So the
 * post-auth / post-payment redirect breaks: the auth/payment succeeds
 * externally but the user is stranded on Safari with iOS complaining
 * "This site is trying to open another application" and then
 * "Something went wrong — the application couldn't be opened".
 *
 * Fix: on native builds, redirect through the PRODUCTION website
 * (BACKEND_URL is `https://app.binshare.co.uk`). Safari opens the
 * relevant callback page there, which either exchanges the auth
 * session or renders the payment confirmation. The user then returns
 * to the native app — the `App.addListener("appStateChange")` hook in
 * consuming pages refetches state so the UI updates.
 */
export function nativeReturnOrigin() {
  if (Capacitor.isNativePlatform()) return BACKEND_URL;
  return window.location.origin;
}

// Back-compat alias for existing Stripe call-sites. Both point to the
// same function; the name is Stripe-specific for historical reasons.
export const stripeReturnOrigin = nativeReturnOrigin;

export const api = axios.create({ baseURL: API });

// Attach the JWT (issued by /api/auth/login or /api/auth/register) to every
// outbound request so authenticated endpoints work without a custom wrapper.
api.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem("binshare_token");
    if (t) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
    }
  } catch {
    // localStorage unavailable — ignore
  }
  return config;
});


/** True when the given user object represents an admin. Used to gate
 *  admin-only UI like map views (product decision — Phase 5.1). */
export const isAdminUser = (u) => !!u && u.role === "admin";


export function getUser() {  try {
    const raw = localStorage.getItem("binshare_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setUser(u) {
  if (u) localStorage.setItem("binshare_user", JSON.stringify(u));
  else localStorage.removeItem("binshare_user");
}

export function setToken(t) {
  if (t) localStorage.setItem("binshare_token", t);
  else localStorage.removeItem("binshare_token");
}

export function getToken() {
  try { return localStorage.getItem("binshare_token") || null; } catch { return null; }
}

export function logout() {
  localStorage.removeItem("binshare_user");
  localStorage.removeItem("binshare_token");
}

export const ROLE_LABEL = {
  homeowner: "Customer",
  business: "Customer",
  carrier: "Licensed Carrier",
  admin: "Admin",
};
